import {
  API_PREFIX,
  paginatedFromDto,
  type HttpClient,
  type PaginatedResult,
} from "@nasam/kernel";
import type {
  AssignableAsset,
  EligibleSupervisor,
  SupervisorAssignmentCreateInput,
  SupervisorAssignmentCreateResult,
  SupervisorAssignmentDeleteResult,
  SupervisorAssignmentDetail,
  SupervisorAssignmentHistoryItem,
  SupervisorAssignmentHistoryQuery,
  SupervisorAssignmentListItem,
  SupervisorAssignmentsListQuery,
  SupervisorAssignmentUpdateInput,
  SupervisorAssignmentUpdateResult,
} from "../domain";
import {
  assignableAssetsResponseDtoSchema,
  createSupervisorAssignmentResponseDtoSchema,
  deleteSupervisorAssignmentResponseDtoSchema,
  eligibleSupervisorsResponseDtoSchema,
  supervisorAssignmentCreateInputDtoSchema,
  supervisorAssignmentDetailResponseDtoSchema,
  supervisorAssignmentHistoryResponseDtoSchema,
  supervisorAssignmentListResponseDtoSchema,
  supervisorAssignmentUpdateInputDtoSchema,
} from "./supervisorAssignmentDtos";
import {
  assignableAssetFromDto,
  eligibleSupervisorFromDto,
  supervisorAssignmentCreateResultFromDto,
  supervisorAssignmentDetailFromDto,
  supervisorAssignmentHistoryItemFromDto,
  supervisorAssignmentListItemFromDto,
} from "./supervisorAssignmentMappers";

const SUPERVISOR_ASSIGNMENTS_PATH = `${API_PREFIX}/supervisor-assignments`;

// Domain sortBy names predate the real list contract (search/filter/sort
// user story was built first) -- translate to the API's own `sortBy` values
// here rather than renaming the domain type and the list screen's column
// ids/schema that already match it.
const LIST_SORT_BY_TO_API: Record<string, string> = {
  supervisorName: "supervisorName",
  assignedAssetsCount: "assetCount",
  updatedAt: "lastUpdatedAt",
};

function listParams(query: SupervisorAssignmentsListQuery | undefined) {
  if (query === undefined) return undefined;
  return {
    page: query.page,
    limit: query.limit,
    sort: query.sort,
    sortBy: query.sortBy === undefined ? undefined : LIST_SORT_BY_TO_API[query.sortBy],
    search: query.search,
    fromLastUpdatedAt: query.updatedFrom,
    toLastUpdatedAt: query.updatedTo,
  };
}

export interface SupervisorAssignmentsHttpRepository {
  list(
    query?: SupervisorAssignmentsListQuery,
  ): Promise<PaginatedResult<SupervisorAssignmentListItem>>;
  listEligibleSupervisors(): Promise<readonly EligibleSupervisor[]>;
  listAssignableAssets(branchId?: string): Promise<readonly AssignableAsset[]>;
  getById(supervisorId: string): Promise<SupervisorAssignmentDetail>;
  create(
    input: SupervisorAssignmentCreateInput,
  ): Promise<SupervisorAssignmentCreateResult>;
  update(
    input: SupervisorAssignmentUpdateInput,
  ): Promise<SupervisorAssignmentUpdateResult>;
  delete(supervisorId: string): Promise<SupervisorAssignmentDeleteResult>;
  listHistory(
    query?: SupervisorAssignmentHistoryQuery,
  ): Promise<PaginatedResult<SupervisorAssignmentHistoryItem>>;
}

export function createSupervisorAssignmentsHttpRepository(
  http: HttpClient,
): SupervisorAssignmentsHttpRepository {
  return {
    async list(query) {
      const response = await http.get(SUPERVISOR_ASSIGNMENTS_PATH, {
        params: listParams(query),
      });
      return paginatedFromDto(
        supervisorAssignmentListResponseDtoSchema.parse(response),
        supervisorAssignmentListItemFromDto,
      );
    },

    async listEligibleSupervisors() {
      const response = await http.get(`${SUPERVISOR_ASSIGNMENTS_PATH}/supervisors`);
      return eligibleSupervisorsResponseDtoSchema
        .parse(response)
        .data.map(eligibleSupervisorFromDto);
    },

    async listAssignableAssets(branchId) {
      const response = await http.get(`${SUPERVISOR_ASSIGNMENTS_PATH}/assets`, {
        params: branchId === undefined ? undefined : { branchId },
      });
      return assignableAssetsResponseDtoSchema
        .parse(response)
        .data.map(assignableAssetFromDto);
    },

    async getById(supervisorId) {
      const response = await http.get(
        `${SUPERVISOR_ASSIGNMENTS_PATH}/supervisors/${supervisorId}`,
      );
      return supervisorAssignmentDetailFromDto(
        supervisorAssignmentDetailResponseDtoSchema.parse(response).data,
      );
    },

    // All-or-nothing write (one transaction server-side): a 409 on a
    // duplicate pair, or a 400 on an ineligible supervisor, is left to
    // propagate as an HttpError rather than caught here -- the backend's
    // `message` is already the exact localized text the UI should show
    // (see errorMessages() in createAxiosHttpClient.ts), and the app-wide
    // MutationCache.onError already turns any thrown Error into a toast.
    async create(input) {
      const response = await http.post(
        SUPERVISOR_ASSIGNMENTS_PATH,
        supervisorAssignmentCreateInputDtoSchema.parse(input),
      );
      return supervisorAssignmentCreateResultFromDto(
        createSupervisorAssignmentResponseDtoSchema.parse(response).data,
      );
    },

    // Replaces the supervisor's complete selection -- same all-or-nothing
    // transaction/409 behavior as create (see the comment above it).
    async update(input) {
      const response = await http.patch(
        SUPERVISOR_ASSIGNMENTS_PATH,
        supervisorAssignmentUpdateInputDtoSchema.parse(input),
      );
      return supervisorAssignmentCreateResultFromDto(
        createSupervisorAssignmentResponseDtoSchema.parse(response).data,
      );
    },

    async delete(supervisorId) {
      const response = await http.delete(
        `${SUPERVISOR_ASSIGNMENTS_PATH}/supervisors/${supervisorId}`,
      );
      const parsed = deleteSupervisorAssignmentResponseDtoSchema.parse(response);
      return { responseMessage: parsed.message };
    },

    async listHistory(query) {
      const response = await http.get(`${SUPERVISOR_ASSIGNMENTS_PATH}/history`, {
        params: query,
      });
      return paginatedFromDto(
        supervisorAssignmentHistoryResponseDtoSchema.parse(response),
        supervisorAssignmentHistoryItemFromDto,
      );
    },
  };
}
